import React from 'react';
import { List, Map } from 'immutable';
import { Link } from 'react-router-dom';
import { Box, Divider, Drawer, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import OrganizationDialog from './OrganizationDialog';
import { ellipsis } from '../../core/util';
import Menu from './Menu';

/**
 * MenuDrawer
 * ==========
 *
 * Sidebar do dashboard. Funciona em dois modos, controlados pela prop `open`
 * vindo do `Dashboard` (que decide com base no breakpoint):
 *
 * - DESKTOP (isLargeScreen=true): Drawer `variant="permanent"` sempre montado.
 *     • `open=true`  → expandido (DRAWER_WIDTH_OPEN), mostra labels + ícones.
 *     • `open=false` → recolhido (DRAWER_WIDTH_CLOSED_DESKTOP), só ícones.
 *
 * - MOBILE  (isLargeScreen=false): Drawer `variant="temporary"` em overlay.
 *     • `open=true`  → desliza pra dentro (DRAWER_WIDTH_OPEN).
 *     • `open=false` → fora da viewport.
 *     • Clique no backdrop → `onClose` → `onToggleDrawer`.
 *
 * O chevron flutuante aparece SÓ em desktop (`isLargeScreen`) e alterna
 * `ChevronLeft`/`ChevronRight` conforme `open`. Em mobile, o toggle é o
 * hambúrguer no `ViewAppBar`.
 */

// Constantes de largura — usadas tanto aqui quanto no RightPanel pra
// que o conteúdo da direita acompanhe perfeitamente o drawer.
export const DRAWER_WIDTH_OPEN = 280;
export const DRAWER_WIDTH_CLOSED_DESKTOP = 72;

function userMenu(userProfile, setDialogOpen) {
  const {
    avatar,
    organization,
    apelido: name,
    numberOfOwnedOrgs,
  } = userProfile;
  const canSwitchOrg = numberOfOwnedOrgs > 1;

  return List([
    Map({
      avatar,
      label: ellipsis(name, 12),
      isUserName: true,
      avatarLetter: name.charAt(0),
      secondaryLabel: ellipsis(organization.get('fantasia'), 14),
      submenu: List([
        canSwitchOrg &&
          Map({
            label: 'Mudar organização',
            onClick: () => setDialogOpen(true),
            isUserName: true,
          }),
        Map({ label: 'Perfil', link: '/perfil/visualizar', isUserName: true }),
        Map({ label: 'Senhas', link: '/senha/mudar', isUserName: true }),
      ]).filter((x) => x),
    }),
  ]);
}

function MenuDrawer(props) {
  const {
    userProfile = {},
    suspendSession,
    onToggleDrawer,
    onCloseDrawer,
    isLargeScreen,
    open,
    menu,
  } = props;
  const theme = useTheme();
  const [dialogIsOpen, setDialogOpen] = React.useState(false);
  const [isHovering, setHovering] = React.useState(false);

  const { numberOfOwnedOrgs, profileType } = userProfile;
  const isComittee = profileType.get('codigo') === 5;
  const restMenu = isComittee ? menu.slice(3) : null;

  // Em desktop, quando o drawer está fechado e o mouse passa sobre ele, expande
  // por cima do conteúdo (sem mexer no RightPanel). Mouse sai → recolhe.
  const hoverExpand = isLargeScreen && !open && isHovering;
  const isExpanded = open || hoverExpand;

  // ROOT width (.MuiDrawer-docked): controla o espaço reservado no flex layout.
  // Depende SÓ de `open` — hover não mexe aqui pra que o RightPanel não se mova.
  const rootWidth = open
    ? DRAWER_WIDTH_OPEN
    : isLargeScreen
    ? DRAWER_WIDTH_CLOSED_DESKTOP
    : DRAWER_WIDTH_OPEN;

  // PAPER width (.MuiDrawer-paper): controla o visual do drawer. Aceita hover.
  // No MUI v5 o paper é position fixed por default — então quando expande além
  // do root via hover, vaza pra direita SOBRE o conteúdo (overlay), sem empurrar.
  const paperWidth = isExpanded
    ? DRAWER_WIDTH_OPEN
    : isLargeScreen
    ? DRAWER_WIDTH_CLOSED_DESKTOP
    : DRAWER_WIDTH_OPEN;

  // Passamos `open: isExpanded` pros itens do menu — o `Menu/index.jsx` lê via
  // destructuring `{ open: drawerOpen }`, então PRECISA sobrescrever `open` aqui
  // (não adianta adicionar uma prop `drawerOpen` extra, ela seria ignorada).
  // Isso faz o submenu (dropdown do Bem-vindo, etc) abrir durante hover-expand.
  const childProps = {
    ...props,
    open: isExpanded,
    drawerOpen: isExpanded,
    isFocused: false,
    switchDrawerFocus: () => null,
  };

  const transitionMixin = {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: isExpanded
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    boxSizing: 'border-box',
  };

  return (
    <>
      <Drawer
        variant={isLargeScreen ? 'permanent' : 'temporary'}
        open={open}
        onClose={onCloseDrawer}
        // MUI v9: `ModalProps` saiu — agora usa slotProps.root.
        // `keepMounted` evita remount toda vez que abre (performance no mobile).
        slotProps={{
          root: { keepMounted: true },
          paper: { elevation: 0 },
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 30, 45, 0.45)',
              // Blur foi removido — em alguns navegadores mobile (Safari iOS)
              // o `backdrop-filter` impedia o drawer de subir corretamente.
            },
          },
        }}
        // Eventos de hover: só relevante em desktop quando drawer fechado.
        onMouseEnter={() => isLargeScreen && !open && setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        sx={{
          width: rootWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          '& .MuiDrawer-paper': {
            width: paperWidth,
            position: hoverExpand ? 'fixed' : undefined,
            top: hoverExpand ? 0 : undefined,
            left: hoverExpand ? 0 : undefined,
            height: hoverExpand ? '100vh' : undefined,
            zIndex: hoverExpand ? theme.zIndex.drawer + 1 : theme.zIndex.drawer,
            // Gradient azul (mesmo do splash inicial) — espelha identidade visual
            background: `
              radial-gradient(ellipse at 0% 0%, rgba(9, 175, 223, 0.35), transparent 55%),
              radial-gradient(ellipse at 100% 100%, rgba(0, 74, 97, 0.45), transparent 55%),
              linear-gradient(180deg, #0080ad 0%, #197997 60%, #004A61 100%)
            `,
            color: '#fff',
            borderRight: 'none',
            boxShadow: hoverExpand
              ? '0 16px 40px rgba(0, 30, 45, 0.30)'
              : '0 0 24px rgba(0, 30, 45, 0.12)',
            ...transitionMixin,
          },
          // Estilos dos itens do Menu — adaptados para fundo escuro azul.
          '& .MuiListItemButton-root': {
            position: 'relative',
            color: 'rgba(255, 255, 255, 0.82)',
            borderRadius: '10px',
            mx: 1,
            my: 0.5,
            py: 1.15,
            // Quando o drawer está RECOLHIDO, centraliza o ícone (único elemento
            // visível). Quando EXPANDIDO, alinha à esquerda com gap natural.
            justifyContent: isExpanded ? 'flex-start' : 'center',
            paddingLeft: isExpanded ? 1.5 : 1,
            paddingRight: isExpanded ? 1.5 : 1,
            transition:
              'background-color 0.18s ease, color 0.18s ease, transform 0.18s ease, padding 0.2s ease',
            // Quando colapsado, ListItemIcon e ListItemAvatar ficam centralizados
            // e sem margem-direita (que reservaria espaço pro texto ausente).
            '& .MuiListItemIcon-root, & .MuiListItemAvatar-root': isExpanded
              ? {}
              : { marginRight: 0, minWidth: 0, justifyContent: 'center' },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.10)',
              color: '#fff',
            },
            // Item ATIVO: fundo branco translúcido + barra esquerda accent +
            // pequena sombra interna pra dar sensação "pressionado/destacado".
            '&.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
              color: '#fff',
              boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.20)' },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: -8,
                top: '20%',
                height: '60%',
                width: 3,
                borderRadius: 2,
                backgroundColor: '#9EE7F5',
                boxShadow: '0 0 8px rgba(158, 231, 245, 0.6)',
              },
            },
            // Foco por teclado (acessibilidade)
            '&.Mui-focusVisible': {
              outline: '2px solid rgba(255,255,255,0.45)',
              outlineOffset: '-2px',
            },
          },
          // Dividers e scrollbar adaptados
          '& .MuiDivider-root': {
            borderColor: 'rgba(255, 255, 255, 0.12)',
            margin: '8px 16px',
          },
          '& ::-webkit-scrollbar': { width: 6 },
          '& ::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.20)',
            borderRadius: 999,
          },
          '& ::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.32)',
          },
        }}
      >
        <Box sx={{ width: 'inherit', overflowY: { xs: 'auto', lg: 'visible' } }}>
          <Box sx={{ display: 'flex',
              padding: '0 8px',
              alignItems: 'center',
              justifyContent: isExpanded ? 'flex-start' : 'center',
              mx: isExpanded ? 2 : 0,
              ...theme.mixins.toolbar, }}>
            <Link
              to="/"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: isExpanded ? 'flex-start' : 'center',
              }}
            >
              <Box sx={isExpanded ? { pl: { xs: 1, lg: 0 } } : { display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isExpanded ? (
                  // Drawer aberto: logo branca horizontal completa (texto + símbolo)
                  <img
                    src="/img/logo_branca.png"
                    style={{
                      width: isLargeScreen ? 200 : 180,
                      filter: 'drop-shadow(0 4px 14px rgba(0, 30, 45, 0.30))',
                    }}
                    alt="Sua Formatura"
                  />
                ) : (
                  // Drawer recolhido: SVG inline com APENAS os 3 arcos
                  // (a "pipa de wifi") + o ponto. Extraídos do logo.svg
                  // original (que também tem um cone/triângulo grande que
                  // dava aparência de "L" quando o ícone era reduzido).
                  // Usa `fill="currentColor"` pra herdar branco do sidebar.
                  <Box
                    component="svg"
                    viewBox="120 90 480 280"
                    role="img"
                    aria-label="Sua Formatura"
                    sx={{
                      width: 32,
                      height: 32,
                      color: '#fff',
                      filter: 'drop-shadow(0 4px 12px rgba(0, 30, 45, 0.35))',
                    }}
                  >
                    {/* Arco superior (sinal mais amplo) */}
                    <path
                      fill="currentColor"
                      d="M160.897 143.088C147.081 145.546 133.969 150.069 137.888 167.67C141.505 183.353 154.667 181.239 167.026 179.862C190.84 177.306 214.754 173.028 238.567 173.471C331.359 175.044 410.938 207.443 475.043 274.304C483.735 283.35 492.828 294.658 508.603 286.742C521.766 279.516 521.917 269.535 506.041 253.017C411.591 154.691 296.141 118.9 160.897 143.088Z"
                    />
                    {/* Arco médio */}
                    <path
                      fill="currentColor"
                      d="M434.852 290.38C366.577 228.336 285.591 203.657 193.703 212.457C180.892 213.637 169.387 220.028 171.598 235.023C173.607 248.543 185.916 250.46 197.17 249.526C238.621 245.093 280.56 249.882 319.854 263.537C353.792 275.803 384.831 294.687 411.089 319.042C420.182 327.4 430.079 329.858 440.278 321.107C448.868 313.782 445.302 305.768 440.278 297.017C438.686 294.643 436.869 292.421 434.852 290.38Z"
                    />
                    {/* Arco inferior */}
                    <path
                      fill="currentColor"
                      d="M366.928 333.152C324.727 301.934 277.652 283.989 224.298 284.825C207.016 285.071 201.791 295.346 206.765 313.044C210.131 314.913 214.602 319.386 219.274 319.681C265.205 322.423 309.539 337.196 347.636 362.453C355.925 367.714 365.119 363.338 370.545 355.718C371.968 354.061 373.012 352.124 373.605 350.039C374.199 347.953 374.329 345.768 373.986 343.629C373.643 341.491 372.835 339.449 371.618 337.641C370.4 335.834 368.801 334.303 366.928 333.152Z"
                    />
                    {/* Ponto (origem do sinal) */}
                    <path
                      fill="currentColor"
                      d="M278.457 351.49C272.787 349.659 266.689 349.517 260.937 351.082C255.185 352.647 250.037 355.848 246.146 360.28C242.256 364.711 239.798 370.174 239.084 375.974C238.37 381.775 239.432 387.652 242.136 392.861C244.839 398.07 249.063 402.376 254.271 405.233C259.479 408.09 265.437 409.369 271.388 408.908C277.34 408.447 283.018 406.267 287.702 402.644C292.386 399.022 295.865 394.119 297.698 388.559C300.142 381.146 299.491 373.088 295.886 366.143C292.281 359.199 286.016 353.931 278.457 351.49Z"
                    />
                  </Box>
                )}
              </Box>
            </Link>
          </Box>
          <Divider sx={{ mx: 2 }} />
          <Menu
            noDefaultIcon
            {...childProps}
            menu={userMenu(userProfile, setDialogOpen)}
          />
          <Divider sx={{ mx: 2 }} />
          <Menu
            {...childProps}
            style={isLargeScreen && !isComittee ? { height: '100%' } : null}
            menu={isComittee ? menu.slice(0, 3) : menu}
          />
          {isComittee && <Divider sx={{ mx: 2 }} />}
          {restMenu && (
            <Menu
              style={isLargeScreen ? { marginBottom: '75px' } : null}
              {...childProps}
              menu={restMenu}
            />
          )}
          <Menu
            {...childProps}
            style={
              isLargeScreen
                ? {
                    width: 'inherit',
                    position: 'fixed',
                    bottom: 0,
                    overflow: 'hidden',
                    paddingTop: 8,
                    paddingBottom: 12,
                    background:
                      'linear-gradient(to bottom, transparent 0%, rgba(0, 74, 97, 0.6) 60%, #004A61 100%)',
                  }
                : undefined
            }
            menu={List([
              Map({
                icon: 'ExitToApp',
                label: 'Sair',
                href: '/logout',
              }),
            ])}
          />
        </Box>
      </Drawer>

      {isLargeScreen && (
        <IconButton
          size="small"
          aria-label={open ? 'Recolher menu' : 'Expandir menu'}
          onClick={onToggleDrawer}
          // Mouse sobre o chevron mantém o hover-expand ativo (botão está "fora"
          // do paper, então sem isso o paper recolheria ao mover mouse até o botão).
          onMouseEnter={() => !open && setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          sx={{
            position: 'fixed',
            top: 72,
            left: isExpanded
              ? DRAWER_WIDTH_OPEN - 14
              : DRAWER_WIDTH_CLOSED_DESKTOP - 14,
            width: 28,
            height: 28,
            padding: 0,
            backgroundColor: '#fff',
            border: '1px solid rgba(0, 128, 173, 0.18)',
            color: '#0080ad',
            boxShadow: '0 4px 14px rgba(0, 30, 45, 0.18)',
            zIndex: theme.zIndex.drawer + 2,
            transition: theme.transitions.create('left', {
              easing: theme.transitions.easing.sharp,
              duration: isExpanded
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
            '&:hover': {
              backgroundColor: '#0080ad',
              color: '#fff',
              boxShadow: '0 6px 18px rgba(0, 128, 173, 0.35)',
            },
          }}
        >
          {/* Ícone reflete o ESTADO REAL (open), não o visual.
              Drawer aberto → clique fecha → ChevronLeft (←).
              Drawer fechado → clique abre/fixa → ChevronRight (→). */}
          {open ? (
            <ChevronLeft fontSize="small" />
          ) : (
            <ChevronRight fontSize="small" />
          )}
        </IconButton>
      )}

      <OrganizationDialog
        numberOfOwnedOrgs={numberOfOwnedOrgs}
        onClose={() => setDialogOpen(false)}
        suspendSession={suspendSession}
        open={dialogIsOpen}
      />
    </>
  );
}

export default React.memo(MenuDrawer);
